// src/pages/programs/ProgramRegistrationsDashboard.jsx
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, orderBy,
  doc, updateDoc, serverTimestamp, writeBatch, addDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import useProgram from '@/hooks/useProgram';
import { formatCents } from '@/utils/programPricing';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  FunnelIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// ── helpers ────────────────────────────────────────────────────────────────────
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

const STATUS_STYLES = {
  confirmed:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  pending:         'bg-amber-50 text-amber-700 border-amber-200',
  waitlist:        'bg-orange-50 text-orange-700 border-orange-200',
  cancelled:       'bg-zinc-100 text-zinc-500 border-zinc-200',
  archived:        'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const STATUS_LABELS = {
  confirmed:       'Confirmed',
  paid:            'Paid',
  pending_payment: 'Pending Payment',
  pending:         'Pending',
  waitlist:        'Waitlist',
  cancelled:       'Cancelled',
  archived:        'Archived',
};

function StatusBadge({ status }) {
  return (
    <span
      className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] ?? 'bg-zinc-100 text-zinc-500 border-zinc-200'}`}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ── stat card ──────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = 'lime' }) {
  const colorMap = {
    lime:    'bg-[#b5f03d]/15 text-[#5a8a1f] ring-[#b5f03d]/40',
    emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    amber:   'bg-amber-50 text-amber-600 ring-amber-200',
    orange:  'bg-orange-50 text-orange-600 ring-orange-200',
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] flex items-center gap-3">
      <div className={`rounded-xl p-2.5 ring-1 ${colorMap[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xl font-bold text-[#0d1f14] leading-none" style={{ fontFamily: HEAD_FONT }}>{value}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ── main ───────────────────────────────────────────────────────────────────────
export default function ProgramRegistrationsDashboard() {
  const { clubId, programId } = useParams();
  const navigate = useNavigate();
  const { program, loading: programLoading } = useProgram(programId);

  const [registrations, setRegistrations] = useState([]);
  const [regsLoading, setRegsLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [addingToRoster, setAddingToRoster] = useState(null); // regId being added
  const [resendingEmail, setResendingEmail] = useState(null); // regId being emailed

  // ── live registrations ───────────────────────────────────────────────────────
  // Must include clubId — registrations rule under Phase 2 lockdown gates
  // club-staff reads on `isClubStaff(resource.data.clubId)`. A programId-only
  // query isn't statically pattern-matchable to the rule, so the listener
  // fails permission-denied and the dashboard renders empty.
  useEffect(() => {
    if (!programId || !clubId) return;
    setRegsLoading(true);
    const q = query(
      collection(db, 'registrations'),
      where('clubId',    '==', clubId),
      where('programId', '==', programId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRegistrations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRegsLoading(false);
      },
      (err) => {
        console.error('[ProgramRegistrationsDashboard]', err);
        setRegsLoading(false);
      }
    );
    return unsub;
  }, [programId, clubId]);

  // ── derived stats ────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const waitlisted = registrations.filter((r) => r.status === 'waitlist');
    const active = registrations.filter(
      (r) => r.status !== 'cancelled' && r.status !== 'archived' && r.status !== 'waitlist'
    );
    const revenue = active.reduce((sum, r) => {
      if (r.status === 'confirmed' || r.status === 'paid') {
        return sum + (r.pricing?.finalAmount ?? r.amountDue ?? 0);
      }
      return sum;
    }, 0);
    const pending = active.filter(
      (r) => r.status === 'pending_payment' || r.status === 'pending'
    ).length;
    return { total: active.length, revenue, pending, waitlist: waitlisted.length };
  }, [registrations]);

  // ── filtered list ────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return registrations.filter((r) => {
      if (filterDivision !== 'all' && r.divisionId !== filterDivision) return false;
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (term) {
        const haystack = [r.playerName, r.parentName, r.parentEmail]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [registrations, search, filterDivision, filterStatus]);

  // ── status update ────────────────────────────────────────────────────────────
  // Mirrors the bulk-cancel guard — single-row cancellations also need
  // a confirm because the row's "Cancelled" pill is right next to the
  // other status flips and easy to misclick. Other status changes are
  // reversible and don't need the friction.
  const updateStatus = async (regId, newStatus) => {
    if (newStatus === 'cancelled') {
      const reg = registrations.find((r) => r.id === regId);
      const playerName = reg?.playerName || 'this registration';
      const ok = typeof window !== 'undefined' && window.confirm(
        `Cancel ${playerName}'s registration?\n\n` +
        `They'll be removed from the active roster and any future installment charges will stop. ` +
        `You can flip them back to confirmed later — but parents won't be notified automatically.`
      );
      if (!ok) return;
    }
    try {
      await updateDoc(doc(db, 'registrations', regId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Status updated to ${STATUS_LABELS[newStatus] ?? newStatus}`);
    } catch (err) {
      console.error('updateStatus error:', err);
      toast.error('Failed to update status');
    }
  };

  // ── bulk status update ───────────────────────────────────────────────────────
  // Cancellation is the destructive case — once a registration is marked
  // cancelled the family loses their roster slot and any auto-charge
  // installments stop. Confirm prompts only appear on cancellation; the
  // confirmed/pending_payment flips are reversible and don't warrant the
  // friction.
  const bulkUpdateStatus = useCallback(async (newStatus) => {
    if (!selectedIds.size) return;
    if (newStatus === 'cancelled') {
      const ok = typeof window !== 'undefined' && window.confirm(
        `Mark ${selectedIds.size} registration${selectedIds.size === 1 ? '' : 's'} as cancelled?\n\n` +
        `Cancelled players are removed from the active roster and any future installment charges stop. ` +
        `You can flip them back to confirmed later, but parents won't be notified automatically — you'll need to message them.`
      );
      if (!ok) return;
    }
    try {
      setBulkUpdating(true);
      const batch = writeBatch(db);
      selectedIds.forEach((id) => {
        batch.update(doc(db, 'registrations', id), {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      toast.success(`${selectedIds.size} registration${selectedIds.size !== 1 ? 's' : ''} updated to ${STATUS_LABELS[newStatus] ?? newStatus}`);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('bulkUpdateStatus error:', err);
      toast.error('Failed to update registrations');
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedIds]);

  // ── add to roster ────────────────────────────────────────────────────────────
  const addToRoster = useCallback(async (reg) => {
    if (!reg.teamId) {
      toast.error('No team assigned to this division');
      return;
    }
    try {
      setAddingToRoster(reg.id);
      // Parse name into first/last
      const nameParts = (reg.playerName || '').trim().split(/\s+/);
      const firstName = nameParts[0] ?? '';
      const lastName  = nameParts.slice(1).join(' ') || '';
      await addDoc(collection(db, 'teams', reg.teamId, 'players'), {
        firstName,
        lastName,
        parentName:  reg.parentName  ?? '',
        parentEmail: reg.parentEmail ?? '',
        parentPhone: reg.parentPhone ?? '',
        language: 'en',
        source: 'program_registration',
        registrationId: reg.id,
        programId: reg.programId ?? null,
        divisionId: reg.divisionId ?? null,
        // Headshot uploaded during registration → shows on the teammates card.
        photoURL: reg.documents?.player_photo?.url || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success(`${reg.playerName} added to team roster!`);
    } catch (err) {
      console.error('addToRoster error:', err);
      toast.error('Failed to add to roster');
    } finally {
      setAddingToRoster(null);
    }
  }, []);

  // ── resend confirmation email ─────────────────────────────────────────────────
  const resendEmail = useCallback(async (reg) => {
    if (!reg.parentEmail) {
      toast.error('No email address on file');
      return;
    }
    try {
      setResendingEmail(reg.id);
      await httpsCallable(getFunctions(), 'sendRegistrationConfirmation')({ registrationId: reg.id });
      toast.success(`Confirmation email resent to ${reg.parentEmail}`);
    } catch (err) {
      console.error('resendEmail error:', err);
      toast.error('Failed to send email');
    } finally {
      setResendingEmail(null);
    }
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.id)));
    }
  };

  // ── CSV export ───────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = [
      ['Player', 'Division', 'Parent', 'Email', 'Phone', 'Status', 'Amount', 'Registered'],
      ...filtered.map((r) => {
        const div = program?.divisions?.find((d) => d.id === r.divisionId);
        const amt = r.pricing?.finalAmount ?? r.amountDue ?? 0;
        const date = toDate(r.createdAt);
        return [
          r.playerName ?? '',
          div?.name ?? r.divisionId ?? '',
          r.parentName ?? '',
          r.parentEmail ?? '',
          r.parentPhone ?? '',
          STATUS_LABELS[r.status] ?? r.status ?? '',
          `$${(amt / 100).toFixed(2)}`,
          date ? format(date, 'yyyy-MM-dd') : '',
        ];
      }),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${program?.name ?? 'program'}-registrations.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── bulk-send action bar ─────────────────────────────────────────────────────
  // Mirrors the pattern Agent D added on RegistrationsDashboard. Programs span
  // multiple teams (one per division), so the click routes to the new club-mode
  // composer (/clubs/:clubId/messages/new) which is multi-team-safe. The
  // composer reads ?programId= and ?audience= and:
  //   • derives the team set from program.divisions[].teamId (no need to pass
  //     ?teams= explicitly)
  //   • narrows the registration audience to families with reg.programId === X
  //     (server-side filter, can't be widened by tampering with the URL)
  //
  // We render the bar only when a meaningful filter narrowing exists — same
  // discipline as RegistrationsDashboard (no bar on the "all rows" view).
  const bulkSendAction = useMemo(() => {
    if (!clubId || !programId) return null;
    const composerPath = `/clubs/${clubId}/messages/new`;
    if (filterStatus === 'pending_payment') {
      const count = filtered.filter((r) => r.status === 'pending_payment').length;
      if (!count) return null;
      return {
        label: `Remind all ${count} awaiting payment`,
        count,
        href: `${composerPath}?type=Payment%20Due&audience=program-registrants&programId=${programId}`,
      };
    }
    // No "missing_docs" filter on this dashboard today — programs use
    // requiredDocTypes per program, not per season, and the registration
    // schema doesn't yet surface a "missing docs" subset client-side.
    // Add when that lands; for now skip the action bar on other filters.
    return null;
  }, [filterStatus, filtered, clubId, programId]);

  // ── render ───────────────────────────────────────────────────────────────────
  if (programLoading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">
          Loading…
        </div>
      </div>
    );
  }

  const divisions = program?.divisions ?? [];

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6 pb-16">
      {/* Back */}
      <Link
        to={`/clubs/${clubId}/programs/${programId}`}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        {program?.name ?? 'Program'}
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
            Program
          </p>
          <h1 className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
            Registrations
          </h1>
          {program && (
            <p className="text-sm text-zinc-500 mt-0.5">{program.name}</p>
          )}
        </div>
        <button
          type="button"
          onClick={exportCSV}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className={`grid gap-3 ${stats.waitlist > 0 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
        <StatCard icon={UserGroupIcon} label="Total Registered" value={stats.total} color="lime" />
        <StatCard icon={ClockIcon}     label="Pending Payment"  value={stats.pending} color="amber" />
        {stats.waitlist > 0 && (
          <StatCard icon={ClockIcon} label="Waitlisted" value={stats.waitlist} color="orange" />
        )}
        <StatCard icon={CurrencyDollarIcon} label="Revenue Collected" value={formatCents(stats.revenue)} color="emerald" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <input
            className="block w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-4 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
            placeholder="Search by player, parent, or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Division filter */}
        {divisions.length > 1 && (
          <select
            value={filterDivision}
            onChange={(e) => setFilterDivision(e.target.value)}
            className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
          >
            <option value="all">All Divisions</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
        >
          <option value="all">All Statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="paid">Paid</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="waitlist">Waitlist</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      {regsLoading ? (
        <div className="text-center py-16 text-zinc-400 text-sm">Loading registrations…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-zinc-200 bg-white">
          <UserGroupIcon className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500 font-medium">No registrations found</p>
          {(search || filterDivision !== 'all' || filterStatus !== 'all') && (
            <button
              onClick={() => { setSearch(''); setFilterDivision('all'); setFilterStatus('all'); }}
              className="text-sm text-[#5a8a1f] hover:text-[#3d6010] hover:underline mt-2 font-semibold"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          {/* Count + bulk action bar */}
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-300 accent-[#b5f03d]"
                checked={selectedIds.size > 0 && selectedIds.size === filtered.length}
                ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filtered.length; }}
                onChange={toggleSelectAll}
                aria-label="Select all"
              />
              <p className="text-sm text-zinc-500">
                {filtered.length} registration{filtered.length !== 1 ? 's' : ''}
                {filtered.length !== registrations.length && ` (filtered from ${registrations.length})`}
              </p>
            </div>

            {/* Bulk actions — only visible when rows are selected */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="text-xs font-bold uppercase text-[#3d6010] bg-[#b5f03d]/15 border border-[#b5f03d]/40 rounded-full px-2.5 py-1"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {selectedIds.size} selected
                </span>
                {['confirmed', 'pending_payment', 'cancelled'].map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={bulkUpdating}
                    onClick={() => bulkUpdateStatus(s)}
                    className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    Mark {STATUS_LABELS[s]}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-zinc-500 hover:text-[#5a8a1f]"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Rows */}
          <div className="divide-y divide-zinc-100">
            {filtered.map((reg) => {
              const div = divisions.find((d) => d.id === reg.divisionId);
              const date = toDate(reg.createdAt);
              const amt = reg.pricing?.finalAmount ?? reg.amountDue ?? 0;
              const isExpanded = expandedId === reg.id;

              return (
                <div key={reg.id}>
                  {/* Main row */}
                  <div className="flex items-center w-full px-5 py-4 hover:bg-[#b5f03d]/5 transition-colors gap-3">
                    {/* Row checkbox */}
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-zinc-300 accent-[#b5f03d] shrink-0"
                      checked={selectedIds.has(reg.id)}
                      onChange={() => toggleSelect(reg.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${reg.playerName}`}
                    />
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : reg.id)}
                      className="flex-1 text-left min-w-0"
                    >
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Player */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0d1f14] truncate">
                          {reg.playerName || '—'}
                        </p>
                        <p className="text-xs text-zinc-400 mt-0.5 truncate">
                          {reg.parentName}{reg.parentEmail ? ` · ${reg.parentEmail}` : ''}
                        </p>
                      </div>

                      {/* Division */}
                      {div && (
                        <span className="hidden sm:block text-xs text-zinc-500 bg-zinc-100 rounded-full px-2.5 py-1 shrink-0">
                          {div.name}
                        </span>
                      )}

                      {/* Status */}
                      <StatusBadge status={reg.status} />

                      {/* Amount */}
                      <div className="text-right shrink-0 min-w-[60px]">
                        <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>{formatCents(amt)}</p>
                        {date && (
                          <p className="text-[11px] text-zinc-400">{format(date, 'MMM d')}</p>
                        )}
                      </div>
                    </div>
                    </button>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 pb-5 bg-zinc-50 border-t border-zinc-100">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                        {/* Left: details */}
                        <div className="space-y-2 text-sm">
                          <DetailRow label="Player" value={reg.playerName} />
                          {reg.playerDob && <DetailRow label="Date of Birth" value={reg.playerDob} />}
                          {reg.playerGender && <DetailRow label="Gender" value={reg.playerGender} />}
                          <DetailRow label="Division" value={div?.name ?? reg.divisionId ?? '—'} />
                          <DetailRow label="Parent" value={reg.parentName} />
                          <DetailRow label="Email" value={reg.parentEmail} />
                          {reg.parentPhone && <DetailRow label="Phone" value={reg.parentPhone} />}
                          <DetailRow label="Registered" value={date ? format(date, 'MMM d, yyyy h:mm a') : '—'} />
                          <DetailRow label="Registration ID" value={reg.id.slice(-8).toUpperCase()} mono />
                        </div>

                        {/* Right: actions */}
                        <div className="space-y-3">
                          <p
                            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                            style={{ fontFamily: HEAD_FONT }}
                          >
                            Update Status
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {['confirmed', 'pending_payment', 'waitlist', 'cancelled'].map((s) => (
                              <button
                                key={s}
                                type="button"
                                disabled={reg.status === s}
                                onClick={() => updateStatus(reg.id, s)}
                                className={clsx(
                                  'rounded-lg px-3 py-1.5 text-xs font-bold uppercase border transition-all',
                                  reg.status === s
                                    ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-default'
                                    : 'bg-white text-[#0d1f14] border-zinc-200 hover:border-[#b5f03d] hover:bg-[#b5f03d]/10'
                                )}
                                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                              >
                                {STATUS_LABELS[s]}
                              </button>
                            ))}
                          </div>

                          {/* Resend email */}
                          {reg.parentEmail && (
                            <div className="mt-4 pt-3 border-t border-zinc-200">
                              <p
                                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
                                style={{ fontFamily: HEAD_FONT }}
                              >
                                Email
                              </p>
                              <button
                                type="button"
                                disabled={resendingEmail === reg.id}
                                onClick={() => resendEmail(reg)}
                                className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 disabled:opacity-50 transition-all"
                                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                              >
                                <EnvelopeIcon className="h-3.5 w-3.5" />
                                {resendingEmail === reg.id ? 'Sending…' : 'Resend Confirmation Email'}
                              </button>
                            </div>
                          )}

                          {/* Roster sync */}
                          {(reg.status === 'confirmed' || reg.status === 'paid') && reg.teamId && (
                            <div className="mt-4 pt-3 border-t border-zinc-200">
                              <p
                                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
                                style={{ fontFamily: HEAD_FONT }}
                              >
                                Roster
                              </p>
                              <button
                                type="button"
                                disabled={addingToRoster === reg.id}
                                onClick={() => addToRoster(reg)}
                                className="inline-flex items-center gap-2 rounded-lg bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] disabled:opacity-50 transition-all active:scale-[0.97]"
                                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                              >
                                <UserGroupIcon className="h-3.5 w-3.5" />
                                {addingToRoster === reg.id ? 'Adding…' : 'Add to Team Roster'}
                              </button>
                              <p className="text-[10px] text-zinc-400 mt-1">
                                Adds {reg.playerName} to the team assigned to this division.
                              </p>
                            </div>
                          )}

                          {/* Waiver signatures */}
                          {reg.waiverSignatures?.length > 0 && (
                            <div className="mt-3">
                              <p
                                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
                                style={{ fontFamily: HEAD_FONT }}
                              >
                                Waivers
                              </p>
                              <div className="space-y-1">
                                {reg.waiverSignatures.map((w) => (
                                  <div key={w.id} className="flex items-center gap-2 text-xs">
                                    {w.signed ? (
                                      <CheckCircleIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                                    ) : (
                                      <XMarkIcon className="h-4 w-4 text-zinc-300 shrink-0" />
                                    )}
                                    <span className={w.signed ? 'text-[#0d1f14]' : 'text-zinc-400'}>
                                      {w.name || 'Waiver'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk-send sticky action bar — only when the active filter narrows
          to an actionable subset (e.g. "pending_payment"). Routes to the
          club-mode ComposerWizard so the message can fan out across every
          team the program spans, not just one. */}
      {bulkSendAction && (
        <div className="sticky bottom-0 left-0 right-0 z-20 mt-4 -mx-4 sm:-mx-6 border-t border-zinc-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-3 shadow-[0_-2px_8px_-2px_rgba(13,31,20,0.08)]">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/15 border border-[#b5f03d]/40 px-2.5 py-1 text-[11px] font-bold uppercase text-[#3d6010]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {bulkSendAction.count} selected
            </span>
            <button
              type="button"
              onClick={() => navigate(bulkSendAction.href)}
              aria-label={bulkSendAction.label}
              className="ml-auto inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_2px_8px_-2px_rgba(181,240,61,0.6)] transition-all active:scale-[0.97] w-full sm:w-auto justify-center"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              {bulkSendAction.label}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// ── local helpers ──────────────────────────────────────────────────────────────
function DetailRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-zinc-400 w-24 shrink-0">{label}</span>
      <span className={clsx('text-[#0d1f14] break-all', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}
